import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useSelectedLocation } from 'contexts';
import { useResponse } from 'contexts/response';
import { ActiveUnit, Booking } from 'types';
import { isBooking } from 'utils';

export const useBookingApi = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useSelectedLocation();
  const { handleError, setSuccess } = useResponse();

  const findBookingByUnitId = useCallback(async (unitId: number): Promise<Booking | null> => {
    try {
      const response = await fetch(new URL(`/api/bookings/units/${unitId}`, window.location.origin), {
        method: 'GET',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const json = await response.json();
        if (isBooking(json)) return json;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  }, []);

  const handleFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const response = await fetch(input, init);
        if (!response.ok) {
          handleError(response);
          return null;
        }
        const json = await response.json();
        if (isBooking(json)) {
          setSuccess(
            <div className="grid-1">
              <span>{json.bookingNumber ?? 'BOOKING NUMBER'}</span>
              {json.bookingDetails?.map(({ count, iso }, i) => (
                <span key={i}>
                  {count} x {iso}
                </span>
              ))}
            </div>
          );
          return json;
        }
        await handleError(response, json);
        return null;
      } catch (e) {
        await handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  const postBooking = useCallback(
    async (booking: Booking): Promise<Booking | null> => {
      const input: URL = new URL('/api/bookings', window.location.origin);
      const init: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(booking)
      };
      return await handleFetch(input, init);
    },
    [handleFetch]
  );

  const putBooking = useCallback(
    async (booking: Booking): Promise<Booking | null> => {
      const input: URL = new URL(`/api/bookings/${booking.id}`, window.location.origin);
      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(booking)
      };
      return await handleFetch(input, init);
    },
    [handleFetch]
  );

  const reserveUnit = useCallback(
    async (bookingId: number, unit: Partial<ActiveUnit>) => {
      const input: URL = new URL(`/api/bookings/${bookingId}/units`, window.location.origin);
      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(unit)
      };
      try {
        const response = await fetch(input, init);
        if (response.ok) {
          const json = await response.clone().json();
          if (isBooking(json)) {
            setSuccess(`${unit.unitNumber ?? 'Unit'} reserved`);
            return json;
          }
        }
        await handleError(response);
      } catch (e) {
        await handleError(e);
      }
      return null;
    },
    [handleError, setSuccess]
  );

  const unreserveUnit = useCallback(
    async (bookingId: number, unitId: number) => {
      const input: URL = new URL(`/api/bookings/${bookingId}/units/${unitId}`, window.location.origin);
      const init: RequestInit = {
        method: 'DELETE',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      try {
        const response = await fetch(input, init);
        if (response.ok) {
          setSuccess(`Unit removed`);
          return true;
        }
        await handleError(response);
      } catch (e) {
        await handleError(e);
      }
      return false;
    },
    [handleError, setSuccess]
  );

  const deleteBooking = useCallback(
    async (bookingId: number) => {
      const headers = new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` });
      const input = new URL(`/api/bookings/${bookingId}`, window.location.origin);
      try {
        const response: Response = await fetch(input, { method: 'DELETE', headers });
        if (response.ok) {
          setSuccess('Booking Deleted');
          navigate(!selectedLocation ? '/bookings' : `/bookings?location=${selectedLocation.id}`);
        } else handleError(response);
      } catch (e) {
        handleError(e);
      }
    },
    [handleError, navigate, selectedLocation, setSuccess]
  );

  return { deleteBooking, findBookingByUnitId, postBooking, putBooking, reserveUnit, unreserveUnit };
};
